'use client';

import React, { useEffect, useState } from "react";
import { usePathname, useRouter } from 'next/navigation'
import Link from "next/link";
import { images } from "@/utils/images";
import { Link as ScrollLink } from "react-scroll";
import { useDisclosure } from "@mantine/hooks";
import { getMenuItems } from "@/utils/menus";
import Hamburger from 'hamburger-react'
import { useAtom } from 'jotai';
import { isDarkModeAtom } from "@/config/store/globalStore";
import LanguageSelector from "@/components/LanguageSelector";

// Define a type for menu items if not already available globally
interface MenuItem {
  label: string;
  id: string;
}

// Helper function to get default menu items (e.g., in Korean or a neutral state)
// This should match the structure of getMenuItems but use a fixed language or keys
const getDefaultMenuItems = (): MenuItem[] => {
  // Assuming 'ko' is the default or server-rendered language for menu items
  // Or use the keys directly if LANGUAGES.MENUBAR is structured to allow it
  // For simplicity, using Korean as the default here.
  // This should ideally fetch from LANGUAGES.MENUBAR[key]['ko']
  return [
    { label: '홈', id: 'home' },
    { label: '블로그', id: 'blogs' },
    { label: '질문', id: 'qna' },
    { label: '캘린더', id: 'calendar' },
  ];
};

export default function NavigationBar() {
  const { push } = useRouter();
  const pathname = usePathname();
  const [opened, { close, open, toggle }] = useDisclosure(false);
  const [isDarkMode, setIsDarkMode] = useAtom(isDarkModeAtom);
  const [hasMounted, setHasMounted] = useState(false); // For hydration fix
  const [currentMenuItems, setCurrentMenuItems] = useState<MenuItem[]>(getDefaultMenuItems()); // Initialize with default
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // 전체 화면 메뉴가 열릴 때 스크롤 비활성화
  useEffect(() => {
    if (opened) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [opened]);
  
  const MobileNavbar = () => {
    return (
    <>
      <nav className="z-[200] w-full flex md:hidden sticky h-12 items-center top-0 bg-white dark:bg-gray-900 justify-between px-4">
        <Link href={'/'} className="flex gap-1.5 items-center" scroll={false}>
          <img className="w-16 h-full" src={images.logo} />
          {/* <div className="font-bold text-sm text-gray-900 dark:text-white">KUXY</div> */}
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-400">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            )}
          </button>
          <Hamburger
            size={20}
            toggled={opened}
            toggle={toggle}
            hideOutline={false}
            color={isDarkMode ? "#ffffff" : "#444444"}
            label="Show menu"
          />
        </div>
      </nav>

      {/* 전체 화면 메뉴 */}
      {opened && (
        <div className="fixed top-12 bottom-0 left-0 right-0 z-[150] flex flex-col items-center justify-center bg-white/40 dark:bg-gray-900/40 backdrop-blur-lg">
          <div className="flex flex-col items-center gap-12">
            {currentMenuItems.map((item) => (
              <Link 
                key={item.id} 
                href={item.id === 'home' ? '/' : `/${item.id}`} 
                onClick={toggle}
              >
                <div
                  className={`text-xl font-medium ${
                    (item.id === 'home' && pathname === '/') || pathname === `/${item.id}`
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  } hover:text-blue-500 dark:hover:text-blue-400 transition-colors`}
                >
                  {item.label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
    // return (
    //   <nav className="z-[100] w-full flex md:hidden sticky h-12 items-center top-0 bg-white justify-between px-4">
    //     <Link href={'/'} className="flex gap-1.5 items-center" scroll={false}>
    //       <img className="w-16 h-full" src={images.logo}/>
    //       {/* <div className="font-bold text-sm text-gray-900">KUXY</div> */}
    //     </Link>
    //     <Hamburger size={20} toggled={opened} toggle={toggle} hideOutline={false} color={"#444444"} label="Show menu"/>
    //   </nav>
    // )
  }


  const DesktopNavBar = () => (
    <nav className="hidden md:flex shadow-xs fixed inset-x-0 top-4 z-40 mx-auto h-[60px] max-w-5xl items-center justify-between rounded-2xl px-8 saturate-100 backdrop-blur-[20px] transition-colors bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800">
      <div
        className="flex gap-2 align-middle items-center cursor-pointer w-[160px]"
        onClick={() => push('/')}
      >
        <img className="w-16 h-full" src={images.logo}/>
        {/* <div className="font-bold text-base dark:text-white">Kuxy</div> */}
      </div>
      <div className="flex gap-4 items-center">
        {currentMenuItems.map((item) => (
          <Link
            key={item.id}
            href={item.id === 'home' ? '/' : `/${item.id}`}
          >
            <div className={`p-2.5 rounded-lg text-base text-gray-600 dark:text-gray-300 cursor-pointer hover:font-semibold hover:text-gray-700 dark:hover:text-white min-w-[54px] text-center ${
              (item.id === 'home' && pathname === '/') || pathname === `/${item.id}` 
                ? 'font-semibold text-gray-700 dark:text-white' 
                : ''
            }`}>
              {item.label}
            </div>
          </Link>
        ))}
        <LanguageSelector />
        <button 
          onClick={toggleDarkMode}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 ml-2"
        >
          {isDarkMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-400">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          )}
        </button>
      </div>
    </nav>
  )

  return (
    <>
      <MobileNavbar/>
      <DesktopNavBar/>
    </>
  );
}